import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../components/Shared/Button";
import Input from "../../components/Shared/Input";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import { toast } from "react-toastify";
import Spinner from "../../components/Shared/Spinner";
import { CreateIFRSQuestionExhibit as CreateExhibitAPI } from "../../api/Quiz/CreateIFRSQuestionExhibit";
import { UpdateIFRSQuestionExhibit } from "../../api/Quiz/UpdateIFRSQuestionExhibit";
import { GetIFRSQuestionExhibits } from "../../api/Quiz/GetIFRSQuestionExhibits";
import { GetIFRSQuestionById } from "../../api/Quiz/GetIFRSQuestionById";
import Upload from "../../components/Shared/Upload";
import { ButtonTypes } from "../../utils/consts";
import { useUserDataStore } from "../../stores/user";

const CreateIFRSQuestionExhibit = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { courseId, exam_id, question_id, exhibit_id } = useParams<{
    courseId: string;
    exam_id: string;
    question_id: string;
    exhibit_id?: string;
  }>();
  const isEditMode = !!exhibit_id;

  const [title, setTitle] = useState("");
  const [selectedType, setSelectedType] = useState<option | null>(null);
  const [filePath, setFilePath] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const typeOptions: option[] = [
    { id: "pdf", name: "PDF" },
    { id: "spreadsheet", name: "Spreadsheet" },
  ];

  // Fetch question details
  const { isLoading: isQuestionLoading } = useQuery({
    queryFn: () => GetIFRSQuestionById(exam_id || "", question_id || ""),
    queryKey: ["ifrs-question", exam_id, question_id],
    enabled: !!exam_id && !!question_id && userRole !== "admin",
    refetchOnWindowFocus: false,

  });

  // Fetch exhibit data if in edit mode
  const { data: ExhibitsResponse, isLoading: isExhibitsLoading } = useQuery({
    queryFn: () =>
      GetIFRSQuestionExhibits(question_id || "", { page: 1, limit: 100 }),
    queryKey: ["ifrs-question-exhibits", question_id],
    enabled: isEditMode && !!question_id,
    refetchOnWindowFocus: false,
  });

  // Populate form when in edit mode
  useEffect(() => {
    if (isEditMode && ExhibitsResponse?.data?.data && exhibit_id) {
      const exhibit = ExhibitsResponse.data.data.find(
        (ex) => ex.id === exhibit_id
      );
      if (exhibit) {
        setTitle(exhibit.title);
        setSelectedType({
          id: exhibit.type,
          name: exhibit.type === "pdf" ? "PDF" : "Spreadsheet",
        });
        setFilePath(exhibit.filePath);
      }
    }
  }, [isEditMode, ExhibitsResponse, exhibit_id]);

  // Create/Update exhibit mutation
  const { mutate: createExhibit } = useMutation({
    mutationFn: (data: {
      title: string;
      type: "pdf" | "spreadsheet";
      filePath?: string;
      file?: File;
    }) => {
      if (!question_id) throw new Error("Question ID is required");
      if (isEditMode && exhibit_id) {
        return UpdateIFRSQuestionExhibit(question_id, exhibit_id, data);
      }
      return CreateExhibitAPI(question_id, data);
    },
    onSuccess: (response) => {
      if (response.error) {
        setIsSubmitting(false);
        toast.error(
          response.error.message ||
          `Failed to ${isEditMode ? "update" : "create"} exhibit`
        );
        return;
      }
      setIsSubmitting(false);
      toast.success(
        `Exhibit ${isEditMode ? "updated" : "created"} successfully`
      );
      queryClient.invalidateQueries({
        queryKey: ["ifrs-question-exhibits", question_id],
      });
      navigate(
        `/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions/${question_id}/exhibits`
      );
    },
    onError: () => {
      setIsSubmitting(false);
      toast.error(`Failed to ${isEditMode ? "update" : "create"} exhibit`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!selectedType) {
      toast.error("Please select a type");
      return;
    }

    // Validation based on type
    if (selectedType.id === "spreadsheet") {
      if (!selectedFile) {
        toast.error("Please select an Excel file");
        return;
      }
    } else if (selectedType.id === "pdf") {
      if (!filePath.trim()) {
        toast.error("Please upload a PDF file");
        return;
      }
    }

    setIsSubmitting(true);
    const formData: {
      title: string;
      type: "pdf" | "spreadsheet";
      filePath?: string;
      file?: File;
    } = {
      title,
      type: selectedType.id as "pdf" | "spreadsheet",
    };

    // Add file or filePath based on type
    if (selectedType.id === "spreadsheet" && selectedFile) {
      formData.file = selectedFile;
    } else if (selectedType.id === "pdf") {
      formData.filePath = filePath;
    }

    createExhibit(formData);
  };

  const handleFileUpload = (url: string) => {
    setFilePath(url);
    setIsUploading(false);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  if (isQuestionLoading || (isEditMode && isExhibitsLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={`/dashboard/courses/${courseId}/ifrs-exam/${exam_id}/questions/${question_id}/exhibits`}
          className="font-semibold text-primary hover:text-primary/80"
        >
          ← Back to Exhibits
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          {isEditMode ? "Edit Exhibit" : "Create Exhibit"}
        </h1>
      </div>

      <div className="p-8 bg-white shadow rounded-xl border-black/15">
        <h2 className="mb-6 text-2xl font-bold">Exhibit Information</h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <Input
              name="title"
              type="text"
              title="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter exhibit title"
              inputStyles="!rounded !h-[50px]"
              isRequired
            />

            <Selectbox
              title="Type"
              selectOptions={typeOptions}
              selected={selectedType}
              setSelected={setSelectedType}
              placeholder="Select type"
              className="!rounded"
            />

            <div className="w-full">
              <label className="block mb-2 font-medium text-burntBlack">
                File <span className="text-red-500">*</span>
              </label>
              {selectedType?.id === "spreadsheet" ? (
                <div>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="w-full h-[50px] px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  {selectedFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>
              ) : (
                <Upload
                  folder="ifrs-exhibits"
                  acceptedFiles={{ "application/pdf": [".pdf"] }}
                  onFileUploaded={handleFileUpload}
                  onUploadStarts={() => setIsUploading(true)}
                  onUploadEnds={() => setIsUploading(false)}
                >
                  <Input
                    name="file"
                    type="text"
                    value={filePath}
                    placeholder={
                      isUploading ? "Uploading..." : "Click to upload PDF file"
                    }
                    inputStyles="!rounded !h-[50px]"
                    upload={true}
                  />
                </Upload>
              )}
              {filePath && selectedType?.id === "pdf" && (
                <a
                  href={filePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm text-primary hover:text-primary/80"
                >
                  View uploaded file
                </a>
              )}
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type={ButtonTypes.SUBMIT}
                variant="primary"
                className="!h-12 w-40 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
                disabled={isSubmitting || isUploading}
              >
                {isSubmitting ? (
                  <Spinner />
                ) : isEditMode ? (
                  "Update Exhibit"
                ) : (
                  "Create Exhibit"
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateIFRSQuestionExhibit;
